import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
// import { isValidFileType, isValidFileSize } from '../../services/api/attachmentService'; // FileAPIを使用するため不要
import { fileUploadService } from '../../services/api/fileUploadService';
import { useChatInputBehavior } from '../../hooks/useChatInputBehavior';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useClipboardImagePaste } from '../../hooks/useClipboardImagePaste';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { setSelectedProduct, clearSelectedProduct, setGenerationMode } from '../../redux/chat/chatSlice';
import { FileUploadProgress } from '../common/FileUploadProgress';
import ProductSelectionModal, { ChatProductSelectionDetail } from './ProductSelectionModal';
import SelectedProductTag from './SelectedProductTag';
import { Product } from '../../types/product';
import { ChatGenerationMode } from '../../types/chat';

interface NewChatInputProps {
  messageInput: string;
  setMessageInput: (value: string) => void;
  isLoading: boolean;
  onSendMessage: (attachments?: File[], uploadedFiles?: any[], productId?: number, generationMode?: ChatGenerationMode) => Promise<void>;
  selectedAgent?: any; // AIエージェントの情報
  disabled?: boolean; // 親コンポーネントから無効化するためのプロパティ
  isStickyDisabled?: boolean; // sticky positionを無効にするためのプロパティ
}

interface AttachmentPreviewProps {
  file: File;
  onRemove: () => void;
}

// === styled-components から移行した Tailwind クラス定義 ===
// 旧 InputContainer / InputForm / InputActionRow / AttachButton / SendButton /
// AttachmentsPreviewContainer / ErrorMessage の 7 定義は JSX から一度も参照されて
// いないデッドコードだったため、移行にあたり削除した（描画結果に影響なし）。

const ATTACHMENT_PREVIEW_ITEM =
  'relative rounded-lg overflow-hidden w-20 h-20 bg-gray-100 border border-gray-200 flex items-center justify-center';

const ATTACHMENT_IMAGE = 'w-full h-full object-cover';

const ATTACHMENT_VIDEO =
  'w-full h-full flex items-center justify-center bg-gray-800 text-white text-[24px]';

const ATTACHMENT_FILE =
  'w-full h-full flex flex-col items-center justify-center bg-gray-50 text-gray-700 text-[32px]';

// 旧 AttachmentFile 内のネスト定義 `.file-extension`（クラス名は互換のため残す）
const FILE_EXTENSION = 'text-[10px] mt-1 font-semibold uppercase';

const ATTACHMENT_INFO =
  'absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 text-center whitespace-nowrap overflow-hidden text-ellipsis';

const REMOVE_BUTTON =
  'absolute top-0.5 right-0.5 bg-black/60 text-white border-0 rounded-full w-5 h-5 text-[12px] flex items-center justify-center cursor-pointer hover:bg-black/80';

const HIDDEN_INPUT = 'hidden';

const UPLOAD_DIALOG =
  'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl p-6 shadow-[0_10px_40px_rgba(0,0,0,0.2)] z-[1000] max-w-[400px] w-[90%]';

const DIALOG_OVERLAY = 'fixed top-0 left-0 right-0 bottom-0 bg-black/50 z-[999]';

const DIALOG_TITLE = 'm-0 mb-3 text-[1.2rem] text-gray-800';

const DIALOG_CONTENT = 'm-0 mb-5 text-gray-500 text-[0.95rem] leading-[1.5]';

const DIALOG_BUTTONS = 'flex gap-3 justify-end';

const DIALOG_BUTTON_BASE =
  'px-4 py-2 rounded-lg text-[0.9rem] cursor-pointer transition-all duration-200 ease-[ease]';
const DIALOG_BUTTON_PRIMARY = 'border-0 bg-blue-500 text-white hover:bg-blue-600';
const DIALOG_BUTTON_DEFAULT = 'border border-gray-200 bg-white text-gray-500 hover:bg-gray-50';

const UPLOADED_FILE_INFO =
  'bg-sky-50 border border-sky-200 rounded-lg p-3 my-2 mx-4 flex items-center gap-3';

const FILE_ICON = 'text-[1.5rem]';

const FILE_DETAILS = 'flex-1';

const FILE_NAME = 'font-semibold text-gray-800 text-[0.9rem]';

const FILE_SERVICE = 'text-gray-500 text-[0.8rem] mt-0.5';

// 添付ファイルのプレビューコンポーネント
const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({ file, onRemove }) => {
  // ファイルのURLを生成
  const fileUrl = URL.createObjectURL(file);
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  const isDocument = fileUploadService.isSupportedDocumentFile(file);

  // ファイルアイコンとラベルを取得
  const fileIcon = fileUploadService.getFileIcon(file);
  const fileExtension = fileUploadService.getFileExtension(file);

  return (
    <div className={ATTACHMENT_PREVIEW_ITEM}>
      {isImage ? (
        <img className={ATTACHMENT_IMAGE} src={fileUrl} alt={file.name} />
      ) : isVideo ? (
        <div className={ATTACHMENT_VIDEO}>🎬</div>
      ) : isDocument ? (
        <div className={ATTACHMENT_FILE}>
          <div>{fileIcon}</div>
          <div className={FILE_EXTENSION}>{fileExtension}</div>
        </div>
      ) : (
        <div className={ATTACHMENT_FILE}>
          <div>📄</div>
          <div className={FILE_EXTENSION}>{fileExtension}</div>
        </div>
      )}
      <div className={ATTACHMENT_INFO}>{file.name}</div>
      <button className={REMOVE_BUTTON} onClick={onRemove}>✕</button>
    </div>
  );
};

const GENERATION_MODES: { mode: ChatGenerationMode; label: string }[] = [
  { mode: 'image', label: '画像' },
  { mode: 'video', label: '動画' },
];

const NewChatInput: React.FC<NewChatInputProps> = ({
  messageInput,
  setMessageInput,
  isLoading,
  onSendMessage,
  selectedAgent,
  disabled = false, // デフォルト値をfalseに設定
  isStickyDisabled = false
}) => {
  const dispatch = useAppDispatch();
  const selectedProduct = useAppSelector((state) => state.chat.selectedProduct);

  // 🔍 DEBUG: selectedProductの変化を監視
  useEffect(() => {
    console.log('🔍 [DEBUG] selectedProduct changed:', selectedProduct);
  }, [selectedProduct]);

  const [attachments, setAttachments] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [pendingLargeFile, setPendingLargeFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  // 画像／動画モード（排他）。選択中の送信は LLM ではなく生成になる。送信後も選択は維持する。
  // 新規チャットの初回送信で入力欄のインスタンスが入れ替わるため、ローカル state ではなく Redux に置く。
  const generationMode = useAppSelector((state) => state.chat.generationMode);
  const [showProductModal, setShowProductModal] = useState(false); // 商品選択モーダルの表示状態
  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AIファイルアップロード用のフック
  const {
    uploadFile: hookUploadFile,
    cancelUpload,
    removeFile,
    clearError,
    reset,
    uploadProgress: hookUploadProgress,
    isUploading: hookIsUploading,
    error: uploadError,
    uploadedFiles
  } = useFileUpload();

  // Redux stateから現在のチャットIDを取得
  const currentChatId = useAppSelector((state) => state.chat?.currentChat?.id || null);


  // 送信処理
  const handleSend = async () => {
    console.log('⚡ [DEBUG] handleSend function called!');

    // エラーがある場合は送信をブロック
    if (error || uploadError) {
      console.log('❌ [DEBUG] Blocked by error:', { error, uploadError });
      return;
    }

    // デバッグ情報を出力
    console.log('🚀 NewChatInput handleSend:', {
      messageInput_length: messageInput.trim().length,
      attachments_count: attachments.length,
      uploadedFiles_count: uploadedFiles.length,
      uploadedFiles_data: uploadedFiles,
      selectedProduct: selectedProduct,
      isLoading
    });

    // メッセージ、添付ファイル、またはselectedProductがある場合に送信可能
    if ((messageInput.trim() || attachments.length > 0 || uploadedFiles.length > 0 || selectedProduct) && !isLoading) {
      // 添付ファイルとアップロード済みファイル情報を送信
      const attachmentsToSend = attachments.length > 0 ? attachments : undefined;
      const uploadedFilesToSend = uploadedFiles.length > 0 ? uploadedFiles : undefined;
      const productIdToSend = selectedProduct?.id;

      console.log('📤 NewChatInput onSendMessage call:', {
        attachmentsToSend,
        uploadedFilesToSend,
        generationMode,
        productIdToSend
      });

      try {
        await onSendMessage(attachmentsToSend, uploadedFilesToSend, productIdToSend, generationMode ?? undefined);

        // 送信成功後にクリア
        setAttachments([]);
        reset(); // アップロード済みファイルもクリア

        // 商品選択もクリア
        if (selectedProduct) {
          dispatch(clearSelectedProduct());
        }

        console.log('✅ NewChatInput: 送信完了後クリア実行');
      } catch (error) {
        console.error('❌ NewChatInput: 送信エラー:', error);
        // エラーの場合はクリアしない
      }
    }
  };

  // 商品選択ハンドラー
  const handleProductSelect = (product: Product, detail?: ChatProductSelectionDetail) => {
    console.log('🔍 [DEBUG] handleProductSelect called:', product);

    // Reduxに商品を保存（セグメント／訴求・メイン画像は任意。商品と一緒に解除される）
    const productData = {
      id: product.id,
      name: product.name,
      segmentAppeal: detail?.segmentAppeal ?? null,
      primaryImage: detail?.primaryImage ?? null,
    };
    console.log('🔍 [DEBUG] Dispatching setSelectedProduct:', productData);
    dispatch(setSelectedProduct(productData));

    setShowProductModal(false);
  };

  // 商品選択解除ハンドラー
  const handleRemoveProduct = () => {
    dispatch(clearSelectedProduct());
  };

  // 統一されたキーボード挙動を使用
  const { handleKeyDown, isComposing, setIsComposing } = useChatInputBehavior({
    onSend: handleSend,
    isLoading,
    disabled,
    canSend: Boolean((messageInput.trim() || attachments.length > 0 || selectedProduct) && !error)
  });

  // 高さの自動調整
  useEffect(() => {
    if (textFieldRef.current) {
      textFieldRef.current.style.height = 'auto';
      textFieldRef.current.style.height = `${textFieldRef.current.scrollHeight}px`;
    }
  }, [messageInput]);

  // 初期ロード時にフォーカス
  useEffect(() => {
    if (textFieldRef.current && !isLoading) {
      textFieldRef.current.focus();
    }
  }, [isLoading]);

  // デバッグ用: コンポーネントマウント時
  useEffect(() => {
    console.log('🔍 [DEBUG] NewChatInput mounted', {
      messageInput,
      isLoading,
      disabled,
      selectedAgent: selectedAgent?.title,
      handleSend: typeof handleSend,
      handleSubmit: typeof handleSubmit
    });
  }, []);

  // デバッグ用: 送信ボタンの状態をリアルタイムで監視
  useEffect(() => {
    const isSendDisabled = isLoading || disabled || isUploading || hookIsUploading ||
      (!messageInput.trim() && attachments.length === 0 && uploadedFiles.length === 0 && !selectedProduct);

    console.log('🔍 [DEBUG] Send button state:', {
      isSendDisabled,
      isLoading,
      disabled,
      isUploading,
      hookIsUploading,
      messageInput: messageInput.substring(0, 20) + '...',
      messageInput_length: messageInput.length,
      attachments_count: attachments.length,
      uploadedFiles_count: uploadedFiles.length,
      hasSelectedProduct: !!selectedProduct
    });
  }, [isLoading, disabled, isUploading, hookIsUploading, messageInput, attachments, uploadedFiles, selectedProduct]);

  // ファイル選択ハンドラー
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => addFiles(event.target.files);

  // ファイル選択・クリップボード貼り付け共通の添付処理
  const addFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;

    setError(null);
    clearError();

    // ファイルのバリデーション
    for (const file of Array.from(files)) {
      // すべてのファイルをFileAPIで処理するため、ファイル形式のチェックは不要
      // ただし、完全に未知のファイルタイプは警告する

      // Excel/PowerPointファイルは常にAIサービスに直接アップロード
      console.log('🔍 NewChatInput: ファイル判定', {
        file_name: file.name,
        file_type: file.type,
        isExcel: fileUploadService.isExcelFile(file),
        isPowerPoint: fileUploadService.isPowerPointFile(file),
        isImage: fileUploadService.isImageFile(file),
        isVideo: fileUploadService.isVideoFile(file),
        isPDF: fileUploadService.isPDFFile(file),
        isDocument: fileUploadService.isDocumentFile(file),
        isSupportedDocument: fileUploadService.isSupportedDocumentFile(file)
      });

      // 画像ファイルの場合は、プレビュー表示とAIアップロードの両方を行う
      if (fileUploadService.isImageFile(file)) {
        console.log('🖼️ NewChatInput: 画像ファイル検出 - プレビュー表示 + AIアップロード処理');

        // 1. まずattachments配列に追加してローカルプレビューを表示
        setAttachments(prev => [...prev, file]);
        console.log('✅ NewChatInput: 画像をattachmentsに追加（プレビュー表示用）');

        // 2. 同時にAIサービスにもアップロード
        const chatIdForUpload = currentChatId || 0;

        let aiModel = selectedAgent?.model_type || 'openai';
        if (aiModel === 'chatgpt' || aiModel.startsWith('o1') || aiModel.startsWith('o3') || aiModel.startsWith('gpt')) {
          aiModel = 'openai';
        } else if (aiModel.startsWith('claude')) {
          aiModel = 'claude';
        } else if (aiModel.startsWith('gemini')) {
          aiModel = 'gemini';
        }

        console.log('🚀 NewChatInput: 画像ファイルのAIアップロード開始', {
          file_name: file.name,
          file_size: file.size,
          ai_model: aiModel,
          chat_id: chatIdForUpload
        });

        try {
          setUploadProgress(0);
          setIsUploading(true);

          const uploadedFile = await hookUploadFile(file, aiModel, chatIdForUpload);
          console.log('✅ 画像ファイルAIアップロード成功:', uploadedFile);

          setIsUploading(false);
          setUploadProgress(100);
        } catch (error) {
          console.error('💥 NewChatInput: 画像ファイルのAIアップロードに失敗:', error);
          // プレビューは表示されたままにする（アップロード失敗でも送信は可能にする場合がある）
          setError('画像のアップロードに失敗しました。再度お試しください。');
          setIsUploading(false);
        }

        continue;
      }

      // 画像以外のサポートされているファイルをFileAPIで処理
      if (fileUploadService.isSupportedDocumentFile(file)) {
        console.log('📊 NewChatInput: サポートファイル検出 - AIアップロード処理開始');
        // チャットIDが存在しない場合でも、OpenAI Files APIにアップロード
        // 一時的なIDを使用（0を使用、バックエンドで適切に処理）
        const chatIdForUpload = currentChatId || 0;

        // AIサービスへの直接アップロードを実行
        setPendingLargeFile(file);

        // ダイアログは表示せず、直接アップロード処理を開始
        // o3やo1などのOpenAIモデルは'openai'にマッピング
        let aiModel = selectedAgent?.model_type || 'openai';
        if (aiModel === 'chatgpt' || aiModel.startsWith('o1') || aiModel.startsWith('o3') || aiModel.startsWith('gpt')) {
          aiModel = 'openai';
        } else if (aiModel.startsWith('claude')) {
          aiModel = 'claude';
        } else if (aiModel.startsWith('gemini')) {
          aiModel = 'gemini';
        }

        console.log('🚀 NewChatInput: Direct AI file upload starting', {
          file_name: file.name,
          file_size: file.size,
          ai_model: aiModel,
          chat_id: chatIdForUpload,
          original_model_type: selectedAgent?.model_type
        });

        try {
          setUploadProgress(0);
          setIsUploading(true);

          // useFileUploadフックを使用してOpenAI Files APIを確実に使用
          let uploadedFile;
          if (file.size > 10 * 1024 * 1024) {
            // 10MB以上はチャンクアップロード（現在は通常のアップロードにフォールバック）
            // TODO: チャンクアップロード対応を追加
            uploadedFile = await hookUploadFile(file, aiModel, chatIdForUpload);
          } else {
            // 10MB以下は通常のアップロード - フックを使用
            uploadedFile = await hookUploadFile(file, aiModel, chatIdForUpload);
          }

          // アップロードが成功した場合、uploadedFilesはuseFileUploadフックで管理される
          console.log('✅ AI file upload success:', uploadedFile);

          // メッセージ入力欄にはファイル情報を追加しない（内部でファイルIDを管理）

          setIsUploading(false);
          setUploadProgress(100);
          setPendingLargeFile(null);
        } catch (error: any) {
          console.error('💥 NewChatInput: AIファイルアップロードに失敗:', error);
          console.error('💥 NewChatInput: エラー詳細:', {
            error_message: error.message,
            error_response: error.response?.data,
            error_status: error.response?.status,
            file_name: file.name
          });
          setError('ファイルのアップロードに失敗しました');
          setIsUploading(false);
        }

        continue;
      }

      // サポートされていないファイルは通常の添付ファイルとして処理（通常ここには来ない）
      console.warn('⚠️ NewChatInput: サポートされていないファイル', {
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        isSupportedDocument: fileUploadService.isSupportedDocumentFile(file)
      });
      // ファイルタイプが空の場合、拡張子から判断
      if (!file.type || file.type === '') {
        const extension = file.name.split('.').pop()?.toLowerCase();
        console.log('📋 NewChatInput: MIMEタイプが空なので拡張子で判断', extension);

        // 拡張子でサポート判定
        const supportedExtensions = [
          'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', // 画像
          'mp4', 'mpeg', 'mpg', 'mov', 'avi', 'wmv', 'webm', // 動画
          'xlsx', 'xls', 'csv', // Excel
          'pptx', 'ppt', 'odp', // PowerPoint
          'pdf', // PDF
          'doc', 'docx', 'txt', 'rtf' // Word
        ];

        if (extension && supportedExtensions.includes(extension)) {
          // 拡張子でサポートされている場合はアップロード処理を続行
          console.log('✅ NewChatInput: 拡張子でサポート判定 - AIアップロード処理開始');
          const chatIdForUpload = currentChatId || 0;

          let aiModel = selectedAgent?.model_type || 'openai';
          if (aiModel === 'chatgpt' || aiModel.startsWith('o1') || aiModel.startsWith('o3') || aiModel.startsWith('gpt')) {
            aiModel = 'openai';
          } else if (aiModel.startsWith('claude')) {
            aiModel = 'claude';
          } else if (aiModel.startsWith('gemini')) {
            aiModel = 'gemini';
          }

          try {
            setUploadProgress(0);
            setIsUploading(true);

            const uploadedFile = await hookUploadFile(file, aiModel, chatIdForUpload);
            console.log('✅ AI file upload success (extension-based):', uploadedFile);

            setIsUploading(false);
            setUploadProgress(100);
            setPendingLargeFile(null);
          } catch (error) {
            console.error('💥 NewChatInput: AIファイルアップロードに失敗 (拡張子判定):', error);
            setError('ファイルのアップロードに失敗しました');
            setIsUploading(false);
          }
          continue;
        }
      }

      setError(`サポートされていないファイル形式です: ${file.name}`);
      continue;
    }

    // ファイル選択後にinputをリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // クリップボードの画像もファイル選択と同じ addFiles に載せる
  const { handlePaste } = useClipboardImagePaste({ onFiles: addFiles, disabled: isLoading || disabled });

  // AIサービスへのファイルアップロード
  const handleAIUpload = async () => {
    if (!pendingLargeFile || !selectedAgent) return;

    setShowUploadDialog(false);

    // AIモデルタイプを取得（openai, claude, gemini）
    // o3やo1などのOpenAIモデルは'openai'にマッピング
    let aiModel = selectedAgent.model_type || 'openai';
    if (aiModel === 'chatgpt' || aiModel.startsWith('o1') || aiModel.startsWith('o3') || aiModel.startsWith('gpt')) {
      aiModel = 'openai';
    } else if (aiModel.startsWith('claude')) {
      aiModel = 'claude';
    } else if (aiModel.startsWith('gemini')) {
      aiModel = 'gemini';
    }

    // チャットIDが存在しない場合は0を使用
    const chatIdForUpload = currentChatId || 0;

    try {
      // 10MB以上の場合もフックを使用
      if (pendingLargeFile.size > 10 * 1024 * 1024) {
        // TODO: チャンクアップロード対応を追加
        await hookUploadFile(pendingLargeFile, aiModel, chatIdForUpload);
      } else {
        await hookUploadFile(pendingLargeFile, aiModel, chatIdForUpload);
      }

      setPendingLargeFile(null);
    } catch (error) {
      console.error('AI file upload failed:', error);
      setError('ファイルのアップロードに失敗しました');
    }
  };

  // 通常の添付として処理
  const handleLocalAttachment = () => {
    if (pendingLargeFile) {
      setAttachments(prev => [...prev, pendingLargeFile]);
      setPendingLargeFile(null);
    }
    setShowUploadDialog(false);
  };

  // 添付ファイル削除ハンドラー
  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // フォーム送信ハンドラー（ボタンクリック用）
  const handleSubmit = (e: React.FormEvent) => {
    console.log('🔘 [DEBUG] Submit button clicked!');
    e.preventDefault();
    console.log('🚀 [DEBUG] Calling handleSend...');
    handleSend();
    console.log('✅ [DEBUG] handleSend called');
  };


  // ファイル選択ダイアログを開く
  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={`p-4 bg-white ${isStickyDisabled ? '' : 'border-t border-[#E2DFD6]'}`}>
      <div className="max-w-4xl mx-auto">
        <div className="relative">
          <div className="bg-white rounded-3xl border border-[#E2DFD6] focus-within:border-[#C8C3B6] focus-within:shadow-lg transition-all duration-200 p-3 space-y-3">
            {/* 添付ファイルのプレビュー */}
            {attachments.length > 0 && (
              <div className="space-y-2">
                {attachments.map((file, index) => (
                  <AttachmentPreview
                    key={index}
                    file={file}
                    onRemove={() => handleRemoveAttachment(index)}
                  />
                ))}
              </div>
            )}

            {/* アップロード済みファイル表示 */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                {uploadedFiles.map((file) => (
                  <div className={UPLOADED_FILE_INFO} key={file.id}>
                    <div className={FILE_ICON}>📊</div>
                    <div className={FILE_DETAILS}>
                      <div className={FILE_NAME}>{file.name}</div>
                      <div className={FILE_SERVICE}>
                        {file.service === 'openai' && 'OpenAI'}
                        {file.service === 'claude' && 'Claude'}
                        {file.service === 'gemini' && 'Gemini'}
                        にアップロード済み
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(file.id)}
                      className="bg-none border-none cursor-pointer text-xl hover:bg-gray-100 rounded p-1"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* SelectedProductTag の表示 */}
            {selectedProduct && (
              <div className="px-3 py-2">
                <SelectedProductTag
                  product={selectedProduct}
                  onRemove={handleRemoveProduct}
                />
              </div>
            )}

            {/* アップロード進捗表示 */}
            {(isUploading || hookIsUploading) && (
              <div className="p-2">
                <FileUploadProgress
                  progress={isUploading ? uploadProgress : hookUploadProgress}
                  fileName={pendingLargeFile?.name || 'ファイル'}
                  onCancel={cancelUpload}
                  status="uploading"
                />
              </div>
            )}

            {/* エラーメッセージ */}
            {(error || uploadError) && (
              <div className="text-red-500 text-sm p-2">{error || uploadError}</div>
            )}

            {/* Text Input Row */}
            <div className="flex items-end">
              <div className="flex-1">
                <textarea
                  ref={textFieldRef}
                  data-tutorial="chat-input"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder={
                    generationMode === 'image'
                      ? "作りたい画像を説明してください...(Shift + Enterキーで改行)"
                      : generationMode === 'video'
                        ? "作りたい動画を説明してください...(Shift + Enterキーで改行)"
                        : attachments.length > 0
                          ? "添付ファイルの説明を追加...(Shift + Enterキーで改行)"
                          : "メッセージを入力...(Shift + Enterキーで改行)"
                  }
                  disabled={isLoading || disabled}
                  rows={1}
                  onKeyDown={handleKeyDown}
                  onCompositionStart={() => setIsComposing(true)}
                  onCompositionEnd={() => setIsComposing(false)}
                  onPaste={handlePaste}
                  className="min-h-[24px] max-h-[200px] resize-none border-0 bg-transparent focus:ring-0 focus:outline-none text-base placeholder:text-gray-500 p-0 w-full"
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement
                    target.style.height = "auto"
                    target.style.height = target.scrollHeight + "px"
                  }}
                />
              </div>
            </div>

            {/* Button Row */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="ml-0 flex flex-wrap items-center gap-2 sm:ml-2">
                {/* 添付ボタン - ChatGPT/OpenAI系モデルのみ表示 */}
                {selectedAgent && (
                  selectedAgent.model_type === 'chatgpt' ||
                  selectedAgent.model_type === 'openai' ||
                  selectedAgent.model_type?.startsWith('gpt') ||
                  selectedAgent.model_type?.startsWith('o1') ||
                  selectedAgent.model_type?.startsWith('o3')
                ) && (
                  <button
                    type="button"
                    onClick={openFileDialog}
                    disabled={isLoading || disabled}
                    className="p-2 h-8 w-8 rounded-full hover:bg-warm-surface transition-colors bg-transparent border-none cursor-pointer"
                    aria-label="ファイルを添付"
                    title="ファイルを添付（画像・動画・Excel・PowerPoint）"
                  >
                    📎
                  </button>
                )}

                {/* 商品選択ボタン - 全エージェントで表示 */}
                <button
                  type="button"
                  onClick={() => setShowProductModal(true)}
                  disabled={isLoading || disabled}
                  className="px-3 h-8 rounded-full transition-colors text-sm font-medium border bg-white hover:bg-warm-surface hover:border-primary/40 text-gray-600 border-warm-border disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="商品選択"
                >
                  📦 商品選択
                </button>

                {/* 画像／動画モード - 全エージェントで表示。排他で、もう一度押すと解除 */}
                {GENERATION_MODES.map(({ mode, label }) => {
                  const active = generationMode === mode;
                  return (
                    <button
                      key={mode}
                      type="button"
                      data-tutorial="chat-mode-toggle"
                      aria-pressed={active}
                      className={`px-3 h-8 rounded-full transition-colors text-sm font-medium border disabled:opacity-50 disabled:cursor-not-allowed ${
                        active
                          ? "bg-primary/10 text-primary hover:bg-primary/15 border-primary/30"
                          : "bg-white hover:bg-warm-surface hover:border-primary/40 text-gray-600 border-warm-border"
                      }`}
                      onClick={() => dispatch(setGenerationMode(active ? null : mode))}
                      disabled={isLoading || disabled}
                      title={`${label}を生成します（クレジットを消費します）`}
                    >
                      {label}
                      {active && <span className="ml-1.5" aria-hidden="true">×</span>}
                    </button>
                  );
                })}
              </div>

              <button
                data-tutorial="chat-send"
                onClick={(e) => {
                  console.log('🔍 [DEBUG] Send button clicked:', {
                    isLoading,
                    disabled,
                    isUploading,
                    hookIsUploading,
                    messageInput_length: messageInput.trim().length,
                    attachments_count: attachments.length,
                    uploadedFiles_count: uploadedFiles.length,
                    selectedProduct,
                    buttonDisabled: isLoading || disabled || isUploading || hookIsUploading || (!messageInput.trim() && attachments.length === 0 && uploadedFiles.length === 0 && !selectedProduct)
                  });
                  handleSubmit(e);
                }}
                className="px-4 h-10 rounded-full bg-primary hover:bg-primary/90 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading || disabled || isUploading || hookIsUploading || (!messageInput.trim() && attachments.length === 0 && uploadedFiles.length === 0 && !selectedProduct)}
              >
                送信
              </button>
            </div>
          </div>

          {/* 隠しファイル入力 */}
          <input
            className={HIDDEN_INPUT}
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            accept="image/*,video/*,.xlsx,.xls,.csv,.pptx,.ppt,.odp,.pdf,.doc,.docx,.txt,.rtf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.oasis.opendocument.presentation,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/rtf"
            multiple
          />
        </div>
      </div>

      {/* アップロードダイアログ */}
      {showUploadDialog && pendingLargeFile && (
        <>
          <div className={DIALOG_OVERLAY} onClick={() => setShowUploadDialog(false)} />
          <div className={UPLOAD_DIALOG}>
            <h3 className={DIALOG_TITLE}>大容量ファイルの処理</h3>
            <p className={DIALOG_CONTENT}>
              ファイルサイズが大きいため、AIサービスに直接アップロードすることをお勧めします。
              <br />
              ファイル: {pendingLargeFile.name}
              <br />
              サイズ: {fileUploadService.formatFileSize(pendingLargeFile.size)}
            </p>
            <div className={DIALOG_BUTTONS}>
              <button className={`${DIALOG_BUTTON_BASE} ${DIALOG_BUTTON_DEFAULT}`} onClick={handleLocalAttachment}>
                通常の添付として処理
              </button>
              <button className={`${DIALOG_BUTTON_BASE} ${DIALOG_BUTTON_PRIMARY}`} onClick={handleAIUpload}>
                AIサービスにアップロード
              </button>
            </div>
          </div>
        </>
      )}

      {/* 商品選択モーダル */}
      <ProductSelectionModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        onSelect={handleProductSelect}
      />
    </div>
  );
};

export default NewChatInput;
